import { createMemo, createSignal, For, Show } from "solid-js"
import { useI18n } from "../context/i18n"
import { useReviewActions, type ReviewFinding } from "../context/review-actions"
import { Icon } from "./icon"

export type ReviewFindingsProps = {
  level?: string
  findings: ReviewFinding[]
  dropped?: number
  status?: string
}

/** Findings arrive ranked; grouping must not reorder them. Files appear in the
 * order their most severe finding does, and within a file the model's order is
 * preserved — a card that silently re-sorts a ranked list lies about severity. */
export function groupByFile(findings: readonly ReviewFinding[]) {
  const groups: { file: string; findings: ReviewFinding[] }[] = []
  const index = new Map<string, number>()
  for (const finding of findings) {
    const file = finding.file || ""
    const at = index.get(file)
    const group = at === undefined ? undefined : groups[at]
    if (!group) {
      index.set(file, groups.length)
      groups.push({ file, findings: [finding] })
      continue
    }
    group.findings.push(finding)
  }
  return groups
}

/** The tail of a path, for the group heading. The full path stays in the
 * title attribute — two findings in same-named files in different directories
 * must not read as one file. */
export function basename(file: string) {
  const cleaned = file.replace(/[\\/]+$/, "")
  const cut = Math.max(cleaned.lastIndexOf("/"), cleaned.lastIndexOf("\\"))
  return cut < 0 ? cleaned : cleaned.slice(cut + 1)
}

function Badge(props: { finding: ReviewFinding }) {
  const i18n = useI18n()
  const outcome = () => props.finding.outcome
  const verdict = () => props.finding.verdict
  return (
    <Show when={outcome() || verdict()}>
      <Show
        when={outcome()}
        fallback={
          <span data-slot="review-findings-badge" data-verdict={verdict()!.toLowerCase()}>
            {verdict() === "CONFIRMED"
              ? i18n.t("ui.tool.reportFindings.verdict.confirmed")
              : i18n.t("ui.tool.reportFindings.verdict.plausible")}
          </span>
        }
      >
        <span data-slot="review-findings-badge" data-outcome={outcome()}>
          {outcome() === "fixed"
            ? i18n.t("ui.tool.reportFindings.outcome.fixed")
            : outcome() === "skipped"
              ? i18n.t("ui.tool.reportFindings.outcome.skipped")
              : i18n.t("ui.tool.reportFindings.outcome.noChange")}
        </span>
      </Show>
    </Show>
  )
}

function Row(props: { finding: ReviewFinding }) {
  const i18n = useI18n()
  const actions = useReviewActions()
  const [open, setOpen] = createSignal(false)

  return (
    <div data-slot="review-findings-row" data-open={open()}>
      <button
        type="button"
        data-slot="review-findings-row-trigger"
        aria-expanded={open()}
        onClick={() => setOpen((value) => !value)}
      >
        <Badge finding={props.finding} />
        <span data-slot="review-findings-claim">{props.finding.short_summary || props.finding.summary}</span>
        <Show when={props.finding.line !== undefined}>
          <span data-slot="review-findings-line">:{props.finding.line}</span>
        </Show>
        <Icon name={open() ? "chevron-down" : "chevron-right"} size="small" />
      </button>

      <Show when={open()}>
        <div data-slot="review-findings-body">
          <Show when={props.finding.summary && props.finding.summary !== props.finding.short_summary}>
            <p data-slot="review-findings-summary">{props.finding.summary}</p>
          </Show>
          <Show when={props.finding.failure_scenario}>
            <p data-slot="review-findings-scenario">{props.finding.failure_scenario}</p>
          </Show>
          <div data-slot="review-findings-row-actions">
            <Show when={props.finding.category}>
              <span data-slot="review-findings-category">{props.finding.category}</span>
            </Show>
            <Show when={actions}>
              <button
                type="button"
                data-action="review-finding-open"
                onClick={() => actions!.openInCode({ file: props.finding.file, line: props.finding.line })}
              >
                <Icon name="open-file" size="small" />
                {i18n.t("ui.tool.reportFindings.action.open")}
              </button>
              <button
                type="button"
                data-action="review-finding-fix"
                onClick={() => actions!.requestFix([props.finding])}
              >
                <Icon name="wrench" size="small" />
                {i18n.t("ui.tool.reportFindings.action.fixOne")}
              </button>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  )
}

export function ReviewFindings(props: ReviewFindingsProps) {
  const i18n = useI18n()
  const actions = useReviewActions()
  const pending = createMemo(() => props.status === "pending" || props.status === "running")
  const findings = createMemo(() => props.findings ?? [])
  const groups = createMemo(() => groupByFile(findings()))

  const heading = createMemo(() => {
    const count = findings().length
    const parts = [i18n.t("ui.tool.reportFindings.title")]
    if (props.level) parts.push(props.level)
    parts.push(i18n.t("ui.tool.reportFindings.count", { count }))
    return parts.join(" · ")
  })

  return (
    <div data-component="review-findings">
      <div data-slot="review-findings-header">
        <Icon name="review-active" size="small" />
        <span data-slot="review-findings-heading">{pending() ? i18n.t("ui.tool.reportFindings.pending") : heading()}</span>
        <Show when={actions && findings().length > 0}>
          <button type="button" data-action="review-findings-fix-all" onClick={() => actions!.requestFix(findings())}>
            {i18n.t("ui.tool.reportFindings.action.fixAll")}
          </button>
        </Show>
      </div>

      <Show when={!pending()}>
        <Show
          when={findings().length > 0}
          fallback={<p data-slot="review-findings-empty">{i18n.t("ui.tool.reportFindings.empty")}</p>}
        >
          <For each={groups()}>
            {(group) => (
              <div data-slot="review-findings-group">
                <button
                  type="button"
                  data-slot="review-findings-file"
                  title={group.file}
                  disabled={!actions || !group.file}
                  onClick={() => actions?.openInCode({ file: group.file, line: group.findings[0]?.line })}
                >
                  {basename(group.file) || i18n.t("ui.tool.reportFindings.unknownFile")}
                </button>
                <For each={group.findings}>{(finding) => <Row finding={finding} />}</For>
              </div>
            )}
          </For>
          <Show when={(props.dropped ?? 0) > 0}>
            <p data-slot="review-findings-dropped">
              {i18n.t("ui.tool.reportFindings.dropped", { count: props.dropped! })}
            </p>
          </Show>
        </Show>
      </Show>
    </div>
  )
}
